import { API_BASE_URL } from '../config/brand';
import { telegram } from '../telegram/bridge';

export type DesignAssetErrorCode = 'design_fetch_failed' | 'design_blob_failed' | 'design_decode_failed'
  | 'design_normalize_failed' | 'editor_design_add_failed' | 'design_memory_pressure' | 'design_aborted' | 'design_stale_operation' | 'design_unknown_error';
export type DesignAssetStage = 'fetch' | 'blob' | 'decode' | 'normalize' | 'aborted' | 'stale' | 'unknown';

interface DesignAssetFailureDetails {
  status?: number;
  mime?: string;
  bytes?: number;
}

export class DesignAssetError extends Error {
  constructor(
    public readonly code: DesignAssetErrorCode,
    public readonly stage: DesignAssetStage,
    public readonly details: DesignAssetFailureDetails = {},
    options?: ErrorOptions,
  ) {
    super(code, options);
    this.name = 'DesignAssetError';
  }
}

const protectedAssetCache = new Map<string, Promise<string>>();
const protectedAssetMetadata = new Map<string, Required<Pick<DesignAssetFailureDetails, 'mime' | 'bytes'>> & { status: number }>();
const invalidationListeners = new Set<(cacheKey: string) => void>();

const safeMime = (value: string | null | undefined): string => {
  const mime = (value || '').split(';', 1)[0].trim().toLowerCase();
  return /^[a-z0-9.+-]+\/[a-z0-9.+-]+$/i.test(mime) ? mime.slice(0, 80) : 'unknown';
};

export function asDesignAssetError(
  cause: unknown,
  code: DesignAssetErrorCode = 'design_unknown_error',
  stage: DesignAssetStage = 'unknown',
  details: DesignAssetFailureDetails = {},
): DesignAssetError {
  if (cause instanceof DesignAssetError) return cause;
  if (cause instanceof Error && cause.name === 'AbortError') return new DesignAssetError('design_aborted', 'aborted', details, { cause });
  if (cause instanceof RangeError || (cause instanceof Error && /out of memory|memory pressure|allocation|not enough memory/i.test(cause.message))) return new DesignAssetError('design_memory_pressure', stage, details, { cause });
  return new DesignAssetError(code, stage, details, { cause });
}

export function staleDesignAssetError(): DesignAssetError {
  return new DesignAssetError('design_stale_operation', 'stale');
}

export function isDesignAssetCancellation(error: unknown): boolean {
  return error instanceof DesignAssetError && (error.code === 'design_aborted' || error.code === 'design_stale_operation');
}

export function designAssetDiagnostic(error: unknown, designId: string): {
  code: DesignAssetErrorCode; stage: DesignAssetStage; designId: string; status?: number; mime?: string; bytes?: number;
} {
  const typed = asDesignAssetError(error);
  const safeDesignId = designId.replace(/[^A-Za-z0-9_.-]/g, '_').slice(0, 80) || 'unknown';
  return {
    code: typed.code,
    stage: typed.stage,
    designId: safeDesignId,
    ...(Number.isInteger(typed.details.status) ? { status: typed.details.status } : {}),
    ...(typed.details.mime ? { mime: safeMime(typed.details.mime) } : {}),
    ...(Number.isFinite(typed.details.bytes) ? { bytes: Math.max(0, Number(typed.details.bytes)) } : {}),
  };
}

function protectedAssetPath(url: string): string | null {
  if (url.startsWith('/api/assets/')) return url;
  if (API_BASE_URL && url.startsWith(`${API_BASE_URL}/api/assets/`)) return url.slice(API_BASE_URL.length);
  return null;
}

export function isProtectedCatalogAsset(url: string): boolean {
  return protectedAssetPath(url) !== null;
}

function assetRequestUrl(url: string): string {
  return url.startsWith('/api/assets/') ? `${API_BASE_URL}${url}` : url;
}

export function catalogAssetCacheKey(url: string): string {
  return protectedAssetPath(url) || url;
}

export function subscribeCatalogAssetInvalidation(listener: (cacheKey: string) => void): () => void {
  invalidationListeners.add(listener);
  return () => invalidationListeners.delete(listener);
}

export function invalidateCatalogAsset(url: string): void {
  if (!isProtectedCatalogAsset(url)) return;
  const key = catalogAssetCacheKey(url);
  const previous = protectedAssetCache.get(key);
  protectedAssetCache.delete(key);
  protectedAssetMetadata.delete(key);
  // Mounted thumbnails may still be painting the old Blob URL. Give every
  // invalidation listener time to swap sources before reclaiming it.
  if (previous) void previous.then((objectUrl) => {
    setTimeout(() => URL.revokeObjectURL(objectUrl), 30_000);
  }).catch(() => undefined);
  for (const listener of invalidationListeners) listener(key);
}

export function catalogAssetMetadata(url: string): DesignAssetFailureDetails {
  return protectedAssetMetadata.get(catalogAssetCacheKey(url)) || {};
}

export function resolveCatalogAsset(url: string): Promise<string> {
  if (!isProtectedCatalogAsset(url)) return Promise.resolve(url);
  const key = catalogAssetCacheKey(url);
  let pending = protectedAssetCache.get(key);
  if (!pending) {
    const created = (async () => {
      let response: Response;
      try {
        response = await fetch(assetRequestUrl(url), {
          cache: 'no-store',
          headers: { 'X-Telegram-Init-Data': telegram.initData() },
        });
      } catch (cause) {
        throw asDesignAssetError(cause, 'design_fetch_failed', 'fetch');
      }
      const responseMime = safeMime(response.headers.get('Content-Type'));
      const responseBytes = Math.max(0, Number(response.headers.get('Content-Length')) || 0);
      if (!response.ok) throw new DesignAssetError('design_fetch_failed', 'fetch', {
        status: response.status,
        ...(responseMime !== 'unknown' ? { mime: responseMime } : {}),
        ...(responseBytes ? { bytes: responseBytes } : {}),
      });
      let blob: Blob;
      try { blob = await response.blob(); }
      catch (cause) {
        throw asDesignAssetError(cause, 'design_blob_failed', 'blob', {
          status: response.status,
          ...(responseMime !== 'unknown' ? { mime: responseMime } : {}),
          ...(responseBytes ? { bytes: responseBytes } : {}),
        });
      }
      const mime = safeMime(blob.type || responseMime);
      const bytes = Math.max(0, Number(blob.size) || 0);
      if (!bytes) throw new DesignAssetError('design_blob_failed', 'blob', { status: response.status, mime, bytes });
      protectedAssetMetadata.set(key, { status: response.status, mime, bytes });
      try { return URL.createObjectURL(blob); }
      catch (cause) { throw asDesignAssetError(cause, 'design_blob_failed', 'blob', { status: response.status, mime, bytes }); }
    })();
    protectedAssetCache.set(key, created);
    void created.catch(() => {
      if (protectedAssetCache.get(key) === created) protectedAssetCache.delete(key);
      protectedAssetMetadata.delete(key);
    });
    pending = created;
  }
  return pending;
}
